import { useRef } from "react";
import { ArrowUpDown, ListFilter, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CANDIDATE_SORT_LABELS,
  CandidateSort,
  EMPTY_CANDIDATE_FILTERS,
  MESSAGED_FILTER_LABELS,
  MessagedFilter,
  REVEALED_FILTER_LABELS,
  RevealedFilter,
  countActiveFilters,
  type CandidateFilters,
  type FilterOption,
} from "./candidate-views";

// Radix Select cannot use an empty string as an item value, so "no filter"
// needs its own sentinel.
const ANY_OPTION_VALUE = "any";

function OptionSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: FilterOption[];
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <Select
        value={value ?? ANY_OPTION_VALUE}
        onValueChange={(next) =>
          onChange(next === ANY_OPTION_VALUE ? null : next)
        }
      >
        <SelectTrigger aria-label={label} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY_OPTION_VALUE}>Any</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function MessagedSelect({
  value,
  onChange,
}: {
  value: MessagedFilter;
  onChange: (value: MessagedFilter) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">Messaging</Label>
      <Select
        value={value}
        onValueChange={(next) => onChange(next as MessagedFilter)}
      >
        <SelectTrigger aria-label="Messaging" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.values(MessagedFilter).map((option) => (
            <SelectItem key={option} value={option}>
              {MESSAGED_FILTER_LABELS[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function RevealedSelect({
  value,
  onChange,
}: {
  value: RevealedFilter;
  onChange: (value: RevealedFilter) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">Revealed</Label>
      <Select
        value={value}
        onValueChange={(next) => onChange(next as RevealedFilter)}
      >
        <SelectTrigger aria-label="Revealed" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.values(RevealedFilter).map((option) => (
            <SelectItem key={option} value={option}>
              {REVEALED_FILTER_LABELS[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CandidateFilterPopover({
  filters,
  onFiltersChange,
  professionalTypes,
  specialties,
}: {
  filters: CandidateFilters;
  onFiltersChange: (filters: CandidateFilters) => void;
  professionalTypes: FilterOption[];
  specialties: FilterOption[];
}) {
  const activeCount = countActiveFilters(filters);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2">
          <ListFilter aria-hidden="true" size={16} />
          Filter
          {activeCount > 0 && (
            <span
              aria-label={`${activeCount} active filters`}
              className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs text-white"
            >
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={16}
        className="w-80 max-w-[calc(100vw-2rem)] space-y-4 rounded-xl border-slate-200 bg-white p-4"
      >
        <OptionSelect
          label="Professional type"
          options={professionalTypes}
          value={filters.professionalTypeId}
          onChange={(professionalTypeId) =>
            onFiltersChange({ ...filters, professionalTypeId })
          }
        />
        <OptionSelect
          label="Specialty"
          options={specialties}
          value={filters.specialtyId}
          onChange={(specialtyId) =>
            onFiltersChange({ ...filters, specialtyId })
          }
        />
        <MessagedSelect
          value={filters.messaged}
          onChange={(messaged) => onFiltersChange({ ...filters, messaged })}
        />
        <RevealedSelect
          value={filters.revealed}
          onChange={(revealed) => onFiltersChange({ ...filters, revealed })}
        />
        {activeCount > 0 && (
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => onFiltersChange(EMPTY_CANDIDATE_FILTERS)}
          >
            Clear filters
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function CandidateSortMenu({
  sort,
  onSortChange,
}: {
  sort: CandidateSort;
  onSortChange: (sort: CandidateSort) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2">
          <ArrowUpDown aria-hidden="true" size={16} />
          Sort
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-xl bg-white">
        <DropdownMenuRadioGroup
          value={sort}
          onValueChange={(next) => onSortChange(next as CandidateSort)}
        >
          {Object.values(CandidateSort).map((option) => (
            <DropdownMenuRadioItem
              key={option}
              value={option}
              className="min-h-11"
            >
              {CANDIDATE_SORT_LABELS[option]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CandidateSearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="relative min-w-0 basis-full sm:max-w-sm sm:basis-0 sm:flex-1">
      <Search
        aria-hidden="true"
        size={16}
        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
      />
      <Input
        ref={inputRef}
        type="search"
        aria-label="Search candidates"
        placeholder="Search candidates"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && value !== "") {
            event.preventDefault();
            onChange("");
          }
        }}
        className="h-11 pl-10 pr-11 [&::-webkit-search-cancel-button]:hidden"
      />
      {value !== "" && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            onChange("");
            inputRef.current?.focus();
          }}
          className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-xl text-slate-500 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <X aria-hidden="true" size={16} />
        </button>
      )}
    </div>
  );
}

export function CandidateListToolbar({
  viewLabel,
  count,
  filters,
  onFiltersChange,
  professionalTypes,
  specialties,
  sort,
  onSortChange,
  searchQuery,
  onSearchQueryChange,
}: {
  viewLabel: string;
  count: number;
  filters: CandidateFilters;
  onFiltersChange: (filters: CandidateFilters) => void;
  professionalTypes: FilterOption[];
  specialties: FilterOption[];
  sort: CandidateSort;
  onSortChange: (sort: CandidateSort) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="text-xl font-semibold tracking-tight text-slate-900">
        {viewLabel} Candidates{" "}
        <span className="text-base font-normal text-slate-500">({count})</span>
      </h2>
      <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-1 sm:flex-nowrap sm:justify-end">
        <CandidateSearchInput
          value={searchQuery}
          onChange={onSearchQueryChange}
        />
        <CandidateFilterPopover
          filters={filters}
          onFiltersChange={onFiltersChange}
          professionalTypes={professionalTypes}
          specialties={specialties}
        />
        <CandidateSortMenu sort={sort} onSortChange={onSortChange} />
      </div>
    </div>
  );
}
